import { z } from "zod";

import { slugString } from "./schemas/slug.js";
import { type Locale } from "./locale.js";
import { pickLocalized } from "./authoredText.js";
import descriptionsJson from "./data/exercise-descriptions.json" with { type: "json" };
import type { LocalizedDescription } from "./schemas/exercise.js";

// Bundled descriptions for the default 88 exercises. Generated by the
// Haiku description pipeline in `tooling/exercise-descriptions/`. Validated
// at module load so any drift surfaces during build, not when a coach
// runs the catalog import.
const DescriptionEntry = z.object({
  en: z.string().min(1),
  it: z.string().min(1),
  fr: z.string().min(1),
  de: z.string().min(1),
  ru: z.string().min(1),
});

// Key the map by the catalog-slug regex — same shape as Exercise.slug.
// A typo like "Bench Press" or "bench_press" in the bundled JSON used
// to load silently and never resolve at lookup time; now it fails at
// module load (build / first import) so the issue surfaces loudly.
const SlugKey = slugString(64);
const Map = z.record(SlugKey, DescriptionEntry);
const parsed = Map.parse(descriptionsJson);

export const exerciseDescriptions: Readonly<
  Record<string, LocalizedDescription>
> = parsed;

export const exerciseDescriptionFor = (
  slug: string,
): LocalizedDescription | null => parsed[slug] ?? null;

// Pick the best-fit locale for a description: requested locale first,
// then any non-empty key. Returns null if the description is empty or
// absent — callers render nothing rather than a placeholder.
//
// Resolution lives in ./authoredText.ts, shared with the program's
// authored fields so the two can't drift. Behaviour here is unchanged:
// with no author locale to consider, the order is still preferred → EN →
// the rest.
export const pickDescription = (
  desc: LocalizedDescription | undefined | null,
  preferred: Locale,
): string | null => pickLocalized(desc, preferred);
